import { useMemo, useState } from 'react';
import {
  Pill, Wallet, Receipt, Gift, RotateCcw, Boxes, TriangleAlert, CalendarDays,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, StatCard } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Badge } from '@/components/ui/Badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { LoadingState, ErrorState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import { cn } from '@/lib/cn';
import { usePharmacyReport } from './usePharmacyReports';
import DailySalesChart from './DailySalesChart';

const iso = (d: Date) => d.toISOString().slice(0, 10);
const daysAgo = (n: number) => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };

const INR = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const INR_WHOLE = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });
const money = (v: string | number | null | undefined) => INR.format(parseFloat(String(v ?? 0)) || 0);
const whole = (v: number) => INR_WHOLE.format(v || 0);
const shortDate = (v: string) => new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });

const PRESETS: Array<{ id: string; label: string; from: () => string; to: () => string }> = [
  { id: 'today', label: 'Today', from: () => iso(new Date()), to: () => iso(new Date()) },
  { id: '7d', label: '7 days', from: () => daysAgo(6), to: () => iso(new Date()) },
  { id: '30d', label: '30 days', from: () => daysAgo(29), to: () => iso(new Date()) },
];

type StockFilter = 'low' | 'expiring' | 'expired' | 'all';

/**
 * The pharmacy's own reconciliation panel, at the bottom of Reports.
 *
 * Deliberately one call and one date range covering six questions a pharmacist
 * asks together at the end of a shift — what sold, what came back, how it was
 * paid for, what is running out, what is about to expire, and where the points
 * stand. Splitting these into six screens would mean picking the same date
 * range six times.
 *
 * The distinction the numbers turn on: **sales are reported gross AND net of
 * refunds**, never silently netted. The original invoice is never rewritten
 * when goods come back (GST law), so a report that only showed one figure would
 * disagree with either the invoice book or the cash drawer, depending on which
 * figure it picked. Both are shown so the difference is visible rather than
 * implied.
 */
export default function PharmacyReportsPanel() {
  const [from, setFrom] = useState(daysAgo(6));
  const [to, setTo] = useState(iso(new Date()));
  const [stockFilter, setStockFilter] = useState<StockFilter>('low');

  const query = usePharmacyReport(from, to);
  const report = query.data;

  const activePreset = PRESETS.find((p) => p.from() === from && p.to() === to)?.id ?? null;

  const stockCounts = useMemo(() => {
    const rows = report?.stock ?? [];
    return {
      all: rows.length,
      low: rows.filter((r) => r.is_low && !r.is_expired).length,
      expiring: rows.filter((r) => r.is_expiring_soon).length,
      expired: rows.filter((r) => r.is_expired).length,
    };
  }, [report?.stock]);

  const stock = useMemo(() => {
    const rows = report?.stock ?? [];
    switch (stockFilter) {
      case 'low': return rows.filter((r) => r.is_low && !r.is_expired);
      case 'expiring': return rows.filter((r) => r.is_expiring_soon);
      case 'expired': return rows.filter((r) => r.is_expired);
      default: return rows;
    }
  }, [report?.stock, stockFilter]);

  /** The single busiest medicine, used to scale the share bars in the table. */
  const topAmount = useMemo(
    () => Math.max(...(report?.by_medicine ?? []).map((m) => parseFloat(m.amount) || 0), 0),
    [report?.by_medicine]
  );
  const paymentTotal = useMemo(
    () => (report?.by_payment_mode ?? []).reduce((s, m) => s + (parseFloat(m.amount) || 0), 0),
    [report?.by_payment_mode]
  );

  const needsAttention = stockCounts.expired + stockCounts.low;

  return (
    <section className="mt-6">
      {/* ── Panel header + range ────────────────────────────────── */}
      <div className="mb-4 flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
        <div>
          <h2 className="flex items-center gap-2 text-base font-bold">
            <Pill size={17} className="text-teal" /> Pharmacy
          </h2>
          <p className="mt-0.5 text-[12px] text-ink-muted">
            Counter sales and visit dispensing together — the same shelf, the same till.
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <div className="inline-flex rounded-lg border border-border p-0.5">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => { setFrom(p.from()); setTo(p.to()); }}
                className={cn(
                  'rounded-md px-3 py-1.5 text-[12px] font-medium transition-colors',
                  activePreset === p.id ? 'bg-teal text-white' : 'text-ink-muted hover:bg-muted'
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
          <FormField label="From">
            <div className="relative">
              <CalendarDays size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted" />
              <Input type="date" className="w-auto pl-8" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
            </div>
          </FormField>
          <FormField label="To">
            <div className="relative">
              <CalendarDays size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted" />
              <Input type="date" className="w-auto pl-8" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
            </div>
          </FormField>
        </div>
      </div>

      {query.isError ? (
        <ErrorState message={query.error instanceof ApiError ? query.error.message : 'Could not load the pharmacy report'} />
      ) : query.isLoading || !report ? (
        <LoadingState label="Loading pharmacy figures…" />
      ) : (
        <div className="space-y-4">
          {/* ── Headline ─────────────────────────────────────────── */}
          <div className="grid gap-4 sm:grid-cols-2 min-[1100px]:grid-cols-4">
            <StatCard accentSide="left" accentColor="var(--color-success)">
              <div className="flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">
                <Wallet size={12} /> Net sales
              </div>
              <div className="mt-1.5 text-[26px] font-bold leading-none">{whole(report.totals.net)}</div>
              <div className="mt-1.5 text-[11px] text-ink-muted">
                {whole(report.totals.total)} billed
                {report.totals.refunds > 0 && <> · <span className="text-danger-ink">{whole(report.totals.refunds)} refunded</span></>}
              </div>
            </StatCard>

            <StatCard accentSide="left" accentColor="var(--teal)">
              <div className="flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">
                <Receipt size={12} /> Bills
              </div>
              <div className="mt-1.5 text-[26px] font-bold leading-none">{report.totals.bill_count}</div>
              <div className="mt-1.5 text-[11px] text-ink-muted">
                {report.totals.bill_count > 0
                  ? `${whole(report.totals.total / report.totals.bill_count)} average`
                  : 'none in this period'}
              </div>
            </StatCard>

            <StatCard accentSide="left" accentColor="var(--color-warn-ink)">
              <div className="flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">
                <Gift size={12} /> Points earned
              </div>
              <div className="mt-1.5 text-[26px] font-bold leading-none">{report.loyalty.points_earned}</div>
              <div className="mt-1.5 text-[11px] text-ink-muted">
                {report.loyalty.points_redeemed} redeemed · {money(report.loyalty.loyalty_value)} given
              </div>
            </StatCard>

            <StatCard accentSide="left" accentColor={needsAttention ? 'var(--color-danger)' : 'var(--color-success)'}>
              <div className="flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">
                <Boxes size={12} /> Stock alerts
              </div>
              <div className="mt-1.5 text-[26px] font-bold leading-none">{needsAttention}</div>
              <div className="mt-1.5 text-[11px] text-ink-muted">
                {stockCounts.expired} expired · {stockCounts.expiring} expiring soon
              </div>
            </StatCard>
          </div>

          {/* ── Trend + payment split ────────────────────────────── */}
          <div className="grid items-start gap-4 min-[1100px]:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
            <Card>
              <CardHeader>
                <CardTitle><Wallet size={15} /> Daily sales</CardTitle>
                <span className="text-[11.5px] text-ink-muted">{shortDate(from)} – {shortDate(to)}</span>
              </CardHeader>

              <DailySalesChart rows={report.daily} />

              {/* The chart's own values, in full, as text. Keeps every figure
                  available without colour or hover, and is where the billed /
                  refunded split lives. */}
              <details className="mt-3 border-t border-line pt-2.5">
                <summary className="cursor-pointer text-[11.5px] text-ink-muted hover:text-ink">
                  Show the day-by-day table
                </summary>
                <div className="mt-2 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Bills</TableHead>
                        <TableHead className="text-right">Billed</TableHead>
                        <TableHead className="text-right">Refunds</TableHead>
                        <TableHead className="text-right">Net</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.daily.map((d) => (
                        <TableRow key={d.date}>
                          <TableCell className="text-[12px]">{shortDate(d.date)}</TableCell>
                          <TableCell className="text-right tabular-nums">{d.bill_count}</TableCell>
                          <TableCell className="text-right tabular-nums">{money(d.total)}</TableCell>
                          <TableCell className="text-right tabular-nums text-danger-ink">
                            {parseFloat(d.refunds) > 0 ? `− ${money(d.refunds)}` : '—'}
                          </TableCell>
                          <TableCell className="text-right font-semibold tabular-nums">{money(d.net)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </details>
            </Card>

            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle><Receipt size={15} /> Collection</CardTitle>
                </CardHeader>
                {report.by_payment_mode.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-[13px] text-ink-muted">
                    Nothing collected in this period
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {report.by_payment_mode.map((m) => {
                      const amt = parseFloat(m.amount) || 0;
                      const share = paymentTotal > 0 ? (amt / paymentTotal) * 100 : 0;
                      return (
                        <div key={m.payment_mode}>
                          <div className="flex items-baseline justify-between gap-2 text-[12.5px]">
                            <span className="font-medium">{m.payment_mode}</span>
                            <span className="tabular-nums font-semibold">{money(m.amount)}</span>
                          </div>
                          {/* A share bar, not a pie: four modes on one axis are
                              read faster as lengths than as angles. */}
                          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                            <div className="h-full rounded-full" style={{ width: `${share}%`, background: 'var(--teal)' }} />
                          </div>
                          <div className="mt-0.5 text-[10.5px] text-ink-muted">
                            {Math.round(share)}% · {m.bill_count} bill{m.bill_count === 1 ? '' : 's'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>

              {report.returns.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle><RotateCcw size={15} /> Returns</CardTitle>
                    <Badge variant="neutral">{report.returns.length}</Badge>
                  </CardHeader>
                  <div className="space-y-2">
                    {report.returns.map((r) => (
                      <div key={r.id} className="rounded-lg border border-border px-3 py-2">
                        <div className="flex items-baseline justify-between gap-2 text-[12px] font-semibold">
                          <span className="font-mono">{r.return_number}</span>
                          <span className="tabular-nums text-danger-ink">− {money(r.refund_amount)}</span>
                        </div>
                        <div className="mt-0.5 text-[11px] text-ink-muted">
                          {r.patient_name} · against <span className="font-mono">{r.bill_number}</span>
                        </div>
                        <div className="mt-0.5 text-[11px] text-ink-muted">{r.reason}</div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          </div>

          {/* ── Medicine-wise ────────────────────────────────────── */}
          <Card>
            <CardHeader>
              <CardTitle><Pill size={15} /> Medicine-wise sales</CardTitle>
              {report.by_medicine.length > 0 && <Badge variant="neutral">{report.by_medicine.length}</Badge>}
            </CardHeader>
            {report.by_medicine.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border px-4 py-7 text-center text-[13px] text-ink-muted">
                Nothing sold in this period
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Medicine</TableHead>
                      <TableHead className="text-right">Sold</TableHead>
                      <TableHead className="text-right">Returned</TableHead>
                      <TableHead className="text-right">Net qty</TableHead>
                      <TableHead className="text-right">Discount</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.by_medicine.map((m) => {
                      const amt = parseFloat(m.amount) || 0;
                      return (
                        <TableRow key={m.description}>
                          <TableCell>
                            <div className="font-medium">{m.description}</div>
                            {/* Share of the busiest line, as a length — the eye
                                ranks the list without reading every number. */}
                            <div className="mt-1 h-1 w-full max-w-[180px] overflow-hidden rounded-full bg-muted">
                              <div
                                className="h-full rounded-full"
                                style={{ width: `${topAmount > 0 ? (amt / topAmount) * 100 : 0}%`, background: 'var(--teal)' }}
                              />
                            </div>
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{m.quantity_sold}</TableCell>
                          <TableCell className="text-right tabular-nums text-ink-muted">{m.quantity_returned || '—'}</TableCell>
                          <TableCell className="text-right font-semibold tabular-nums">{m.quantity_net}</TableCell>
                          <TableCell className="text-right tabular-nums text-ink-muted">
                            {parseFloat(m.discount) > 0 ? money(m.discount) : '—'}
                          </TableCell>
                          <TableCell className="text-right font-semibold tabular-nums">{money(m.amount)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>

          {/* ── Stock ────────────────────────────────────────────── */}
          <Card>
            <CardHeader>
              <div className="min-w-0">
                <CardTitle><Boxes size={15} /> Current stock</CardTitle>
                {/* A snapshot of now, not of the date range — "what was on the
                    shelf last Tuesday" is not a question this data can answer. */}
                <div className="mt-0.5 text-[11px] text-ink-muted">
                  as it stands today, not over the range above
                  {report?.near_expiry_days != null && <> · expiring soon = within {report.near_expiry_days} days</>}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {([
                  ['low', 'Low', stockCounts.low],
                  ['expiring', 'Expiring soon', stockCounts.expiring],
                  ['expired', 'Expired', stockCounts.expired],
                  ['all', 'All', stockCounts.all],
                ] as Array<[StockFilter, string, number]>).map(([id, label, count]) => (
                  <Button key={id} size="sm" variant={stockFilter === id ? 'primary' : 'ghost'} onClick={() => setStockFilter(id)}>
                    {label}
                    <span className={cn('tabular-nums', stockFilter === id ? 'opacity-80' : 'text-ink-muted')}>{count}</span>
                  </Button>
                ))}
              </div>
            </CardHeader>

            {stock.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border px-4 py-7 text-center">
                <div className="text-[13px] text-ink-muted">
                  {stockFilter === 'all' ? 'No stock recorded at this branch' : 'Nothing in this category ✓'}
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Medicine</TableHead>
                      <TableHead>Batch</TableHead>
                      <TableHead>Expiry</TableHead>
                      <TableHead className="text-right">Available</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stock.map((s) => (
                      <TableRow key={s.id}>
                        <TableCell>
                          <div className="text-[12.5px] font-medium">{s.generic_name} {s.strength || ''}</div>
                          {s.brand_name && <div className="text-[10.5px] text-ink-muted">{s.brand_name}</div>}
                        </TableCell>
                        <TableCell className="font-mono text-[11px]">{s.batch_number}</TableCell>
                        <TableCell className="text-[12px] tabular-nums">{shortDate(s.expiry_date)}</TableCell>
                        <TableCell className="text-right font-semibold tabular-nums">{s.quantity_available}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {s.is_expired && (
                              <Badge variant="danger" className="inline-flex items-center gap-1">
                                <TriangleAlert size={10} /> Expired
                              </Badge>
                            )}
                            {!s.is_expired && s.is_expiring_soon && <Badge variant="warn">Expiring soon</Badge>}
                            {s.is_low && !s.is_expired && <Badge variant="warn">Low · below {s.low_stock_threshold}</Badge>}
                            {!s.is_expired && !s.is_expiring_soon && !s.is_low && <Badge variant="success">OK</Badge>}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </div>
      )}
    </section>
  );
}
